import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAsync } from './use-async';

export interface Page<T> {
  items: readonly T[];
  total: number;
  /** Offset of the next page; null = last page. Omitted: derived from `total`. */
  nextOffset?: number | null;
}

export interface PagedList<T> {
  /** All loaded rows (pages appended, local replacements/removals applied). */
  items: T[];
  total: number;
  loading: boolean;
  error: unknown;
  loadingMore: boolean;
  hasMore: boolean;
  /** Loads the next page (no-op while loading or at the end). */
  loadMore: () => void;
  /** Starts again from the first page. */
  reload: () => void;
  /** Replaces a loaded row after a write (same id). */
  replace: (item: T) => void;
  /** Hides a loaded row that left the list on the server (deleted, moved). */
  remove: (id: string | number) => void;
}

type Id = string | number;

interface Appended<T> {
  key: string;
  items: T[];
  /** Server offset of the next page, null at the end. */
  nextOffset: number | null;
  /** Removals already accounted for in `nextOffset`. */
  shift: number;
  loading: boolean;
}

interface Local<T> {
  key: string;
  replaced: ReadonlyMap<Id, T>;
  removed: ReadonlySet<Id>;
}

function nextOf(offset: number, page: Page<unknown>): number | null {
  if (page.nextOffset !== undefined) return page.nextOffset;
  const end = offset + page.items.length;
  return page.items.length > 0 && end < page.total ? end : null;
}

/**
 * Server-paged list (T1 "Load more", T10 infinite scroll): the first page loads via useAsync and
 * re-runs when `key` changes (folder, domain, search …); further pages are appended by
 * `loadMore`. Writes are reflected locally with `replace`/`remove` without a reload; removed rows
 * shift the next server offset so no row is skipped. `getId` must be stable (module-level).
 */
export function usePagedList<T>(
  loadPage: (offset: number) => Promise<Page<T>>,
  key: string,
  getId: (item: T) => Id,
  onLoadMoreError?: (error: unknown) => void,
): PagedList<T> {
  const first = useAsync(() => loadPage(0), key);
  const [appended, setAppended] = useState<Appended<T>>();
  const [local, setLocal] = useState<Local<T>>();
  const callbacks = useRef({ loadPage, onLoadMoreError });
  useEffect(() => {
    callbacks.current = { loadPage, onLoadMoreError };
  });

  const firstPage = first.data;
  const extra = appended?.key === key ? appended : undefined;
  const current = local?.key === key ? local : undefined;
  const removedCount = current?.removed.size ?? 0;

  const items = useMemo(() => {
    const seen = new Set<Id>();
    const rows: T[] = [];
    for (const item of [...(firstPage?.items ?? []), ...(extra?.items ?? [])]) {
      const itemId = getId(item);
      if (seen.has(itemId) || current?.removed.has(itemId)) continue;
      seen.add(itemId);
      rows.push(current?.replaced.get(itemId) ?? item);
    }
    return rows;
  }, [firstPage, extra, current, getId]);

  const nextOffset = extra ? extra.nextOffset : firstPage ? nextOf(0, firstPage) : null;
  const shift = extra?.shift ?? 0;
  const loadingMore = extra?.loading ?? false;

  const loadMore = useCallback(() => {
    if (loadingMore || nextOffset === null) return;
    const before = extra?.items ?? [];
    const offset = Math.max(0, nextOffset - (removedCount - shift));
    setAppended({ key, items: before, nextOffset, shift, loading: true });
    callbacks.current.loadPage(offset).then(
      (page) => setAppended({ key, items: [...before, ...page.items], nextOffset: nextOf(offset, page), shift: removedCount, loading: false }),
      (error: unknown) => {
        setAppended({ key, items: before, nextOffset, shift, loading: false });
        callbacks.current.onLoadMoreError?.(error);
      },
    );
  }, [key, extra, nextOffset, shift, removedCount, loadingMore]);

  const reloadFirst = first.reload;
  const reload = useCallback(() => {
    setAppended(undefined);
    setLocal(undefined);
    reloadFirst();
  }, [reloadFirst]);

  const update = useCallback(
    (change: (state: Local<T>) => Local<T>) => setLocal((state) => change(state?.key === key ? state : { key, replaced: new Map(), removed: new Set() })),
    [key],
  );
  const replace = useCallback(
    (item: T) => update((state) => ({ ...state, replaced: new Map(state.replaced).set(getId(item), item) })),
    [update, getId],
  );
  const remove = useCallback((id: Id) => update((state) => ({ ...state, removed: new Set(state.removed).add(id) })), [update]);

  return {
    items,
    total: Math.max(0, (firstPage?.total ?? 0) - removedCount),
    loading: first.loading,
    error: first.error,
    loadingMore,
    hasMore: nextOffset !== null,
    loadMore,
    reload,
    replace,
    remove,
  };
}
